/**
 * plugins/badges-group.ts — Container de badges (div.badges)
 *
 * Ferramentas: adicionar badge com preset ou cor custom.
 * Nota: clique em badge INDIVIDUAL é capturado pelo BadgePlugin primeiro.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';

export class BadgesGroupPlugin implements VtbPlugin {
    readonly panelId = 'vtb-badges-group';
    readonly selectorHints = ['div.badges', '.badges', '[data-vtb-id="badges"]'];
    readonly contextAffinity = ['vtb-cover', 'vtb-doc-header', 'vtb-titles'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            e.classList.contains('badges') || e.dataset.vtbId === 'badges'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Grupo de Badges', el, container: el };
    }

    activate(_ctx: SmartCtx, registry: IVtbRegistry): void {
        // Reset form
        const form = registry.q<HTMLElement>('vtb-badges-add-form');
        if (form) form.style.display = 'none';
        const custom = registry.q<HTMLElement>('vtb-badges-custom');
        if (custom) custom.style.display = 'none';
    }

    setup(registry: IVtbRegistry): void {
        // Toggle form
        registry.q('vtb-badges-add-open')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-badges-add-form');
            if (f) f.style.display = f.style.display === 'none' ? 'flex' : 'none';
        });

        // Preset → mostra/oculta cor custom
        registry.q<HTMLSelectElement>('vtb-badges-preset')?.addEventListener('change', () => {
            const isCustom = registry.q<HTMLSelectElement>('vtb-badges-preset')?.value === 'custom';
            const cf = registry.q<HTMLElement>('vtb-badges-custom');
            if (cf) cf.style.display = isCustom ? 'flex' : 'none';
        });

        registry.q('vtb-badges-confirm')?.addEventListener('click', () => this._add(registry));
        registry.q('vtb-badges-cancel')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-badges-add-form');
            if (f) f.style.display = 'none';
        });
    }

    private _add(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const preset = registry.q<HTMLSelectElement>('vtb-badges-preset')?.value ?? 'new';
        const text   = registry.q<HTMLInputElement>('vtb-badges-text')?.value?.trim() || 'Badge';

        const span = document.createElement('span');
        span.classList.add('badge');

        const PRESET_MAP: Record<string, string> = {
            new: 'badge-new', change: 'badge-change',
            ok: 'badge-ok',   remove: 'badge-remove',
        };
        if (PRESET_MAP[preset]) {
            span.classList.add(PRESET_MAP[preset]);
        } else {
            // Custom
            span.style.background = registry.q<HTMLInputElement>('vtb-badges-custom-bg')?.value || '#e3f2fd';
            span.style.color      = registry.q<HTMLInputElement>('vtb-badges-custom-fg')?.value || '#0d47a1';
        }
        span.textContent = text;
        ctx.el.appendChild(span);

        // Reset form
        const f  = registry.q<HTMLElement>('vtb-badges-add-form');
        const ti = registry.q<HTMLInputElement>('vtb-badges-text');
        if (f)  f.style.display = 'none';
        if (ti) ti.value = '';

        registry.sync();
        registry.selectEl(span);
    }
}
